import { SDK_PROTOCOL } from '@ogm/contracts/bridge';
import { HostBridge } from './bridge.js';
import { GamepadPoller } from './gamepad.js';
import { TouchOverlay } from './overlay.js';
import { SettingsController } from './settings.js';
import { ViewportTracker } from './viewport.js';
export class HostError extends Error {
    code;
    constructor(code) {
        super(code);
        this.code = code;
        this.name = 'HostError';
    }
}
export function createGameFrame(options) {
    let gamesOrigin;
    let appOrigin;
    try {
        gamesOrigin = new URL(options.gamesOrigin);
        appOrigin = new URL(options.appOrigin ?? location.origin);
    }
    catch {
        throw new HostError('invalid-origin');
    }
    if (!['http:', 'https:'].includes(gamesOrigin.protocol) || !['http:', 'https:'].includes(appOrigin.protocol)) {
        throw new HostError('invalid-origin');
    }
    if (gamesOrigin.origin === appOrigin.origin)
        throw new HostError('same-origin');
    const wrapper = document.createElement('div');
    Object.assign(wrapper.style, { position: 'relative', width: '100%', height: '100%' });
    const iframe = document.createElement('iframe');
    iframe.title = 'Game';
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-pointer-lock');
    iframe.setAttribute('allow', 'autoplay; fullscreen; gamepad');
    iframe.setAttribute('referrerpolicy', 'origin');
    Object.assign(iframe.style, { border: '0', width: '100%', height: '100%', display: 'block' });
    const path = `/g/${encodeURIComponent(options.gameId)}/${encodeURIComponent(options.buildId)}/index.html`;
    const source = new URL(path, gamesOrigin.origin);
    source.searchParams.set('build', options.build);
    source.searchParams.set('protocol', String(SDK_PROTOCOL));
    iframe.src = source.href;
    const layer = document.createElement('div');
    wrapper.append(iframe, layer);
    options.container.append(wrapper);
    let disposed = false;
    const dispose = () => {
        if (disposed)
            return;
        disposed = true;
        gamepads.stop();
        overlay.dispose();
        viewport.dispose();
        bridge.close();
        wrapper.remove();
    };
    const bridge = new HostBridge({
        iframe,
        origin: gamesOrigin.origin,
        role: options.role,
        readyTimeoutMs: options.readyTimeoutMs,
        onFatal: dispose,
        hello: {
            protocol: SDK_PROTOCOL,
            capabilities: options.capabilities,
            mode: options.mode,
            build: options.build,
            playerIndex: options.playerIndex,
            tier: options.tier,
        },
    });
    bridge.onError((failure) => options.onError?.(failure));
    const overlay = new TouchOverlay({
        layer,
        inputs: { actions: {} },
        touch: 'tap',
        playerIndex: options.playerIndex,
        onFrame: (frame) => bridge.send('input.frame', frame),
    });
    const gamepads = new GamepadPoller({ onActivity: (at) => overlay.noteGamepadInput(at) });
    gamepads.onFrame((frame) => bridge.send('input.frame', frame));
    const viewport = new ViewportTracker({
        container: options.container,
        send: (value) => bridge.send('viewport', value),
    });
    const settings = new SettingsController((value) => bridge.send('settings', value));
    bridge.on('ready', ({ manifest, marks }) => {
        gamepads.setMapping(options.inputMapping?.gamepad, manifest.inputs.actions);
        overlay.setInputs(manifest.inputs, options.inputMapping?.touch ?? defaultTouchMode(manifest));
        viewport.sendNow();
        settings.resend();
        options.onReady?.(manifest, marks);
    });
    bridge.on('save.write', (payload) => options.onSaveWrite?.(payload));
    bridge.on('event', ({ kind, payload }) => options.onEvent?.(kind, payload));
    bridge.on('load.progress', (payload) => options.onLoad?.({ t: 'load.progress', payload }));
    bridge.on('load.done', (payload) => options.onLoad?.({ t: 'load.done', payload }));
    bridge.on('audio.relay', (payload) => options.onAudioRelay?.(payload));
    bridge.on('input.rawKey', ({ code, down }) => {
        overlay.noteKeyboardInput();
        options.onRawKey?.(code, down);
    });
    gamepads.setMode(options.mode === 'edit' ? 'edit' : 'play');
    overlay.setEnabled(options.mode === 'play');
    gamepads.start();
    return {
        iframe,
        bridge,
        gamepads,
        overlay,
        viewport,
        send: bridge.send.bind(bridge),
        request: bridge.request.bind(bridge),
        on: bridge.on.bind(bridge),
        setMode: (mode) => {
            if (!bridge.send('mode.set', { mode }))
                return;
            gamepads.setMode(mode);
            overlay.setEnabled(mode === 'play');
        },
        setSettings: (value) => settings.set(value),
        dispose,
    };
}
function defaultTouchMode(manifest) {
    return Object.values(manifest.inputs.actions).some((kind) => kind === 'stick') ? 'stick' : 'tap';
}
//# sourceMappingURL=frame.js.map